import { SalesIntakeDto } from '@wisenet/salesapp-client'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import DOMPurify from 'dompurify'
import { useMemo } from 'react'
import 'react-loading-skeleton/dist/skeleton.css'
import styled, { css } from 'styled-components'
import { Button } from '../../elements/Button'
import { useWindowSize } from '../../hooks/useWindowSize'
import { useAppSelector } from '../../types/assets'
import { ApplicationPeriod } from './ApplicationPeriod'
import { Duration } from './Duration'
import { IntakeDate } from './IntakeDate'
import { IntakeStatus } from './IntakeStatus'
import { IntakeField } from './_styles'

dayjs.extend(isBetween)

export type IntakeCardProps = {
  intake: SalesIntakeDto
  type?: 'short' | 'detailed' | 'compact'
  onRemove?: (guid: string) => void
}

type StyledIntakeCardProps = {
  type?: 'short' | 'detailed' | 'compact'
  width?: number
}

export const StyledIntakeCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  overflow: hidden;
  background-color: ${({ type }: StyledIntakeCardProps) =>
    type === 'detailed' ? '#fff' : type === 'compact' ? '#fcfcfc' : '#f9f9f9'};
  padding: 20px 30px;
  transform: scale(1.01);
  margin-bottom: 40px;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
  ${({ type }: StyledIntakeCardProps) =>
    type === 'short'
      ? css`
          display: grid;
          grid-template-columns: 1fr;
          @media (min-width: 765px) {
            grid-template-columns: 3fr 1fr;
          }
        `
      : undefined};

  .intake-header {
    ${({ type }: StyledIntakeCardProps) =>
      type === 'short'
        ? css`
            grid-column: 1 / 4;
          `
        : undefined};
    padding-bottom: 10px;
    margin-bottom: 10px;

    .name {
      font-size: 14pt;
    }

    hr {
      border-top: 1px solid ${props => props.theme.highlightColor};
      border-bottom: none;
      width: 100%;
    }
  }

  .intake-details {
    min-height: ${({ type }: StyledIntakeCardProps) => (type === 'compact' ? undefined : '150px')};
    display: flex;
    flex-direction: column;
    ${({ type }: StyledIntakeCardProps) =>
      type === 'short'
        ? css`
            grid-column: 1 / 3;
          `
        : undefined};

    ${({ type }: StyledIntakeCardProps) =>
      type === 'compact'
        ? css`
            @media (min-width: 765px) {
              display: grid;
              grid-template-columns: 4fr 5fr;
            }
          `
        : undefined};

    /* ${IntakeField} {
          > span {
            margin-right: 5px;
          }
        } */
  }

  .button-panel {
    text-align: right;
    display: flex;
    flex-direction: column;
    margin-bottom: auto;
    div {
      text-align: right;
      margin-bottom: 12%;
    }
    label {
      display: block;
      font-size: 11pt;
      color: ${props => props.theme.fontColor};
      margin-bottom: 5px;
    }
  }

  .delete-panel {
    text-align: center;
    grid-column: 1/4;
    margin-top: 0.5rem;
    color: #cd0505;
    cursor: pointer;

    .fa {
      margin-right: 5px;
    }
  }

  @media only screen and (max-width: 600px) {
    margin-bottom: 30px;
    padding: 20px 15px;

    .intake-header {
      padding-bottom: 0;
      margin-bottom: 0;
    }
    .intake-details {
      min-height: unset;
      margin-top: 13px;
    }

    .button-panel {
      grid-column: 1 / 4;
      align-items: center;
      div {
        margin: 10px 0 0 0;
      }
    }

    .app-period {
      display: none;
    }
  }
`

const getApplicationDateIsInRange = (intake: SalesIntakeDto) => {
  return dayjs().isBetween(
    intake?.applicationAllowStartDate ?? dayjs().subtract(1, 'days'),
    intake?.applicationAllowEndDate ?? dayjs().add(1, 'days'),
  )
}

export const getButtonTooltip = (intake: SalesIntakeDto, buttonName: string = 'Apply Now') => {
  return intake?.vacancyCount! <= 0 && getApplicationDateIsInRange(intake)
    ? `${buttonName} Unavailable - There are no vacancies`
    : `${buttonName} Unavailable - Application dates are closed`
}

export const IntakeCard = ({ intake, onRemove, type = 'short' }: IntakeCardProps) => {
  const path = useAppSelector(state => state.settings.config.path)

  const { width } = useWindowSize()

  const applicationDateIsInRange = useMemo(() => getApplicationDateIsInRange(intake), [intake])

  const disableButton = !applicationDateIsInRange || (intake?.vacancyCount as number) <= 0

  const tooltipText = useMemo(() => (disableButton ? getButtonTooltip(intake) : undefined), [intake, disableButton])

  const onItemRemove = () => {
    onRemove?.(intake.intakeGuid!)
  }

  const cleanedPublishInfo = DOMPurify.sanitize(intake?.publishInfo || '', {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target'],
  })

  return (
    <StyledIntakeCard
      type={type}
      width={width}
    >
      <div className="intake-header">
        <label className="name">{intake?.publishName}</label>
        {width > 600 && <IntakeStatus vacancyCount={intake?.vacancyCount!} />}
        <hr />
      </div>
      {width <= 600 && <IntakeStatus vacancyCount={intake?.vacancyCount!} />}
      <div className="intake-details">
        {intake?.publishDatesFlag && <IntakeDate intake={intake} />}
        <Duration intake={intake} />
        {(type === 'detailed' || width <= 600) && (
          <ApplicationPeriod
            intake={intake}
            type={type}
          />
        )}

        {intake?.location && (
          <IntakeField>
            <label>
              <span>Location:</span> {intake?.location}
            </label>
          </IntakeField>
        )}
        {intake?.salesIntakeDeliveryOptions?.length! > 0 && (
          <IntakeField>
            <span>Delivery Options:</span>{' '}
            {intake?.salesIntakeDeliveryOptions?.map((i, x) => {
              return `${i.deliveryOptionName} ${x !== intake?.salesIntakeDeliveryOptions?.length! - 1 ? ' / ' : ''}`
            })}
          </IntakeField>
        )}

        {intake?.publishInfo && (
          <IntakeField>
            <span>Details: </span>
            <div
              className="publish-info"
              dangerouslySetInnerHTML={{ __html: cleanedPublishInfo }}
            />
          </IntakeField>
        )}

        {intake?.publishPricingInfo && (
          <IntakeField>
            <span>Pricing: </span> {intake?.publishPricingInfo}
          </IntakeField>
        )}
      </div>
      {type === 'short' && (
        <div className="button-panel">
          <Button
            label="APPLY NOW"
            disabled={disableButton}
            tooltip={tooltipText}
            to={disableButton ? '' : `/${path}/intake/${intake?.intakeGuid}`}
          />
          {width >= 600 && <ApplicationPeriod intake={intake} />}
        </div>
      )}
      {type === 'compact' && (
        <div className="delete-panel">
          <span onClick={onItemRemove}>
            <i className="fa fa-trash" />
            Remove
          </span>
        </div>
      )}
    </StyledIntakeCard>
  )
}
